import React, { useState } from 'react';
import { X, Calendar } from 'lucide-react';
import Button from '../shared/Button';
import ModalShell from '../shared/ModalShell';
import DatePicker from './DatePicker';
import { useRequestEventDateChange } from '../../services/bookingDateChangeService';

const EventDateChangeRequestModal = ({
  isOpen,
  onClose,
  bookingId,
  currentEventDate,
  onSuccess,
}) => {
  const [requestedDate, setRequestedDate] = useState('');
  const [clientNote, setClientNote] = useState('');
  const [error, setError] = useState('');

  const mutation = useRequestEventDateChange({
    onSuccess: () => {
      setRequestedDate('');
      setClientNote('');
      setError('');
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.requested_event_date?.[0] ||
          'Failed to submit request'
      );
    },
  });

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().slice(0, 10);

  const formatDisplayDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!requestedDate) {
      setError('Please select a new event date.');
      return;
    }
    mutation.mutate({
      bookingId,
      requested_event_date: requestedDate,
      client_note: clientNote.trim() || undefined,
    });
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="event-date-change-title"
      panelClassName="max-w-md"
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl relative overflow-hidden">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1 !rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2 mb-1 pr-8">
            <Calendar className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h2
              id="event-date-change-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-50"
            >
              Request a new event date
            </h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Your vendor will review this request. The booking date changes only after they approve it.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Current event date</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">{formatDisplayDate(currentEventDate)}</p>
          </div>

          <div>
            <DatePicker
              label="New event date"
              value={requestedDate}
              onChange={(value) => {
                setRequestedDate(value);
                if (error) setError('');
              }}
              placeholder="Select new event date"
              minDate={minDateStr}
              allowPastDates={false}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Note to vendor (optional)
            </label>
            <textarea
              value={clientNote}
              onChange={(e) => setClientNote(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Share a short reason, for example a venue or guest conflict..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 text-sm"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/50 rounded-xl px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="!rounded-xl">
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending} className="!rounded-xl">
              Send request
            </Button>
          </div>
        </form>
      </div>
    </ModalShell>
  );
};

export default EventDateChangeRequestModal;
